import {
  Divider,
  EyeClosedIcon,
  EyeIcon,
  GKButton,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  SimpleLoader,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { KRA_KYC_SETTINGS } from "utils/constants";
import { refetchQuery } from "utils/helpers";

interface FormValues {
  username: string;
  poscode: string;
  password: string;
  aesKey: string;
  apiKey: string;
  sftpPassword: string;
  kraProvider: string;
  clientCode: string;
}

const KYCRegistrationAgency = () => {
  const userData = useAppSelector((state) => state?.user?.userData);

  const [showPassword, setShowPassword] = useState(false);
  const [showSftpPassword, setShowSftpPassword] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      username: "",
      poscode: "",
      password: "",
      aesKey: "",
      apiKey: "",
      sftpPassword: "",
      kraProvider: "",
      clientCode: "",
    },
  });

  const { isFetching } = useFetchQuery({
    key: ["GET_COMPANY_KRA_SETTINGS", userData?.companyId],
    route: `company/${userData?.companyId}/kra-configurations/`,
    success: (res: { data: { data: FormValues } }) => {
      form.reset(res.data.data);
    },
  });

  const { mutate } = useMutation({
    mutationFn: (data: FormValues) =>
      axiosInstance.patch(
        `company/${userData?.companyId}/kra-configurations/`,
        data,
      ),
    onSuccess: () => {
      refetchQuery("GET_COMPANY_KRA_SETTINGS");
    },
  });

  const handleFormSubmit = (values: FormValues) => {
    mutate(values);
  };

  const selectedKraProvider = form.watch("kraProvider");

  return isFetching ? (
    <SimpleLoader />
  ) : (
    <form onSubmit={form.handleSubmit(handleFormSubmit)}>
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Typography fontWeight={600} fontSize={16}>
            KRA configuration
          </Typography>
          <Divider />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="kraProvider"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select KRA Provider",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                inputLabel="KRA Provider"
                disableClearable={false}
                options={KRA_KYC_SETTINGS}
                value={
                  KRA_KYC_SETTINGS.find(
                    (dataItem) => dataItem.value === field.value,
                  ) || null
                }
                onChange={(e, val) => {
                  const selectedProvider = val?.value ?? "";
                  form.reset({
                    kraProvider: selectedProvider,
                    clientCode: "",
                    username: "",
                    poscode: "",
                    password: "",
                    aesKey: "",
                    apiKey: "",
                    sftpPassword: "",
                  });
                }}
                getOptionLabel={(option) => option.name || option}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>

        {selectedKraProvider === "CAMS" && (
          <Grid2 size={4}>
            <Controller
              name="clientCode"
              control={form.control}
              rules={{
                required: {
                  value: selectedKraProvider === "CAMS",
                  message: "Please enter Client Code",
                },
              }}
              render={({ field, fieldState }) => (
                <GKTextField
                  {...field}
                  inputLabel={"Client Code"}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid2>
        )}

        {selectedKraProvider && (
          <>
            <Grid2 size={4}>
              <Controller
                name="username"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter Client ID",
                  },
                }}
                render={({ field, fieldState }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"Name / Client ID"}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="poscode"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter Poscode",
                  },
                }}
                render={({ field, fieldState }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"Poscode"}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="password"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter Password",
                  },
                }}
                render={({ field, fieldState }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"Password"}
                    type={showPassword ? "text" : "password"}
                    slotProps={{
                      input: {
                        style: { paddingRight: 12 },
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ padding: 0 }}
                          >
                            {showPassword ? <EyeIcon /> : <EyeClosedIcon />}
                          </IconButton>
                        ),
                      },
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="aesKey"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter AES",
                  },
                }}
                render={({ field, fieldState }) => (
                  <GKTextField
                    {...field}
                    inputLabel="AES Key/Enc Dec Key"
                    tooltipTitle="AES Key/ Encrypted Decrypted Key"
                    placeholder="AES Key/Enc Dec Key"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name="apiKey"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter API",
                  },
                }}
                render={({ field, fieldState }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"API Key/Secret Key"}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="sftpPassword"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter SFTP password",
                  },
                }}
                render={({ field, fieldState }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"SFTP password"}
                    type={showSftpPassword ? "text" : "password"}
                    slotProps={{
                      input: {
                        style: { paddingRight: 12 },
                        endAdornment: (
                          <IconButton
                            onClick={() =>
                              setShowSftpPassword(!showSftpPassword)
                            }
                            style={{ padding: 0 }}
                          >
                            {showSftpPassword ? <EyeIcon /> : <EyeClosedIcon />}
                          </IconButton>
                        ),
                      },
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid2>
          </>
        )}

        {selectedKraProvider && (
          <Grid2 size={12} textAlign="right">
            <GKButton variant="contained" type="submit">
              Submit
            </GKButton>
          </Grid2>
        )}
      </Grid2>
    </form>
  );
};

export default KYCRegistrationAgency;
